// v2 camel — a proper bactrian: tall legs with knobby knee balls and broad
// two-toe hooves, a long barrel with a pale belly, two pronounced humps capped
// with darker wool tufts, an S-curved neck (two bent segments) with a shaggy
// throat fringe, a small head with the drooping muzzle, little ears and a
// tasselled tail. Two coats: sandy and dark russet. Ported 1:1 from age-of-mcp's
// v2/camel.ts, using the library's shared `critter` (quadLegs) and `wildland`
// (eyes, tube) helpers.
import * as THREE from "three";
import { defineModel, critter, wildland, type Part } from "../../lib";

const COATS = [
  { coat: "#cda86e", tuft: "#9d7a44", belly: "#dcbf86", muzzle: "#b8945c",
    leg: "#c09a60", hoof: "#7e603a" },
  { coat: "#b3884e", tuft: "#835f30", belly: "#c4a068", muzzle: "#a07c46",
    leg: "#a67e44", hoof: "#6e5232" },
];

function camelParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.068, dz: 0.032, bodyY: 0.205, rTop: 0.0105, rFoot: 0.008,
    color: c.leg, hoof: { color: c.hoof, r: 0.011, h: 0.014 },
  });
  // knobby knee balls halfway down each leg
  for (const sx of [1, -1] as const) {
    for (const sz of [1, -1] as const) {
      parts.push({ geometry: new THREE.IcosahedronGeometry(0.0105, 0),
        color: c.coat, position: [sx * 0.068, 0.1, sz * 0.032] });
    }
  }
  parts.push(
    // long barrel + pale belly
    { geometry: new THREE.IcosahedronGeometry(0.054, 0), color: c.coat,
      position: [0, 0.205, 0], scale: [1.7, 0.9, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: c.belly,
      position: [0, 0.185, 0], scale: [1.5, 0.8, 0.9] },
    // the two humps, each capped with a darker wool tuft
    { geometry: new THREE.IcosahedronGeometry(0.027, 0), color: c.coat,
      position: [0.032, 0.262, 0], scale: [1.0, 1.1, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.0255, 0), color: c.coat,
      position: [-0.042, 0.258, 0], scale: [1, 1.1, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.012, 0), color: c.tuft,
      position: [0.032, 0.289, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.012, 0), color: c.tuft,
      position: [-0.042, 0.284, 0] },
    // S-curved neck with a shaggy throat fringe
    wildland.tube([0.082, 0.232, 0], [0.115, 0.278, 0], 0.0205, 0.0165, c.coat),
    wildland.tube([0.115, 0.278, 0], [0.134, 0.326, 0], 0.0165, 0.014, c.coat),
    { geometry: new THREE.IcosahedronGeometry(0.013, 0), color: c.tuft,
      position: [0.105, 0.247, 0], scale: [0.9, 1.4, 0.6] },
    // small head with the drooping muzzle
    { geometry: new THREE.IcosahedronGeometry(0.0215, 0), color: c.coat,
      position: [0.148, 0.338, 0], scale: [1.35, 0.9, 0.8] },
    { geometry: new THREE.ConeGeometry(0.0105, 0.028, 4), color: c.muzzle,
      position: [0.172, 0.329, 0], rotation: [0, 0, -1.75] },
    // tasselled tail
    wildland.tube([-0.088, 0.225, 0], [-0.1, 0.17, 0.006], 0.0035, 0.0025, c.leg),
    { geometry: new THREE.IcosahedronGeometry(0.0075, 0), color: c.tuft,
      position: [-0.102, 0.162, 0.007] },
  );
  for (const s of [1, -1] as const) {
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.005, 0),
      color: c.coat, position: [0.138, 0.356, s * 0.0135] });
  }
  parts.push(...wildland.eyes(0.154, 0.347, 0.0125, 0.0038, "#1e160c"));
  return parts;
}

export const camelV2 = defineModel({
  id: "camel-v2",
  scale: 2.29, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Camel (v2)",
  tags: ["creature","camel","low-poly","age-of-mcp","fable"],
  summary: "Low-poly camel.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.16,
  fits: ["draft"],
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => camelParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => camelParts(1) },
  ],
  animations: [],
});
